import { useCallback, useEffect, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import api from "api/axios";
import Listtable from "components/common/ListTable";
import {
  deleteSpecialityTreatment,
  downloadSpecialityTreatmentFile,
  getSpecialityTreatments,
  importSpecialityTreatments,
} from "api/specialityTreatment";
import { STATUS_OPTIONS } from "config/dentalMasterOptions";
import SpecialityTreatmentModal from "./SpecialityTreatmentModal";
import { importFailures, importSummary } from "./specialityTreatmentForm";

export const TABLE_HEADERS = [
  "Speciality",
  "Treatment Name",
  "Sub-Treatments",
  "Status",
  "Display Order",
  "Action",
];

const TABLE_KEYS = [
  "specialityLabel",
  "name",
  "subTreatmentsCell",
  "statusCell",
  "displayOrderLabel",
  "action",
];

const btnStyle = {
  width: "max-content",
  padding: "0.5rem 2rem",
  color: "#194AF5",
  bgcolor: "#E6EBFF",
  boxShadow: "none",
  borderRadius: "8px",
  ":hover": { bgcolor: "#E6EBFF", color: "#194AF5", boxShadow: "none" },
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const SubTreatmentList = ({ subTreatments = [] }) => (
  <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, py: 0.5 }}>
    {subTreatments.map((sub) => (
      <Box
        component="li"
        key={sub._id || sub.name}
        sx={{ color: sub.isActive === false ? "#9E9E9E" : "inherit", lineHeight: 1.6 }}
      >
        {sub.name}
        {sub.isActive === false ? " (inactive)" : ""};
      </Box>
    ))}
  </Box>
);

const SpecialityTreatmentMaster = () => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTreatment, setActiveTreatment] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [specialityOptions, setSpecialityOptions] = useState([]);
  const fileInputRef = useRef(null);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [specialityFilter, setSpecialityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
      setPageNumber(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    api
      .get("/specialization")
      .then((res) => {
        const list = res?.data?.data?.Specialization;
        setSpecialityOptions(
          (Array.isArray(list) ? list : [])
            .slice()
            .sort((a, b) => String(a?.name).localeCompare(String(b?.name)))
        );
      })
      .catch(() => toast.error("Could not load specialities"));
  }, []);

  const filterParams = useCallback(
    () => ({
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(specialityFilter ? { speciality: specialityFilter } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
    }),
    [debouncedSearch, specialityFilter, statusFilter]
  );

  const fetchTreatments = useCallback(() => {
    setLoading(true);

    getSpecialityTreatments({
      page: pageNumber + 1,
      perPage: rowsPerPage,
      ...filterParams(),
    })
      .then((response) => {
        const payload = response?.data || {};
        const list = Array.isArray(payload.data) ? payload.data : [];

        setRows(
          list.map((item) => ({
            ...item,
            subTreatmentsCell: <SubTreatmentList subTreatments={item.subTreatments} />,
            statusCell: (
              <Chip
                size="small"
                label={item.isActive === false ? "Inactive" : "Active"}
                color={item.isActive === false ? "default" : "success"}
                variant="outlined"
              />
            ),
            displayOrderLabel: String(Number(item.displayOrder) || 0),
            action: (
              <Box sx={{ display: "flex", gap: "0.25rem" }}>
                <IconButton
                  aria-label={`Edit ${item.name}`}
                  onClick={() => {
                    setActiveTreatment(item);
                    setModalOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" color="primary" />
                </IconButton>
                <IconButton
                  aria-label={`Delete ${item.name}`}
                  onClick={() => setDeleteTarget(item)}
                >
                  <DeleteOutlineIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            ),
          }))
        );

        const total = Number(payload?.meta?.total);
        setCount(Number.isFinite(total) ? total : list.length);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Could not load specialty treatments.");
      })
      .finally(() => setLoading(false));
  }, [pageNumber, rowsPerPage, filterParams]);

  useEffect(() => {
    fetchTreatments();
  }, [fetchTreatments]);

  const handleModalClose = (reason = "save") => {
    setModalOpen(false);
    setActiveTreatment({});
    if (reason !== "close") fetchTreatments();
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;

    setBusy(true);
    try {
      await deleteSpecialityTreatment(deleteTarget._id);
      toast.success("Treatment deleted successfully");

      if (rows.length === 1 && pageNumber > 0) {
        setPageNumber(pageNumber - 1);
      } else {
        fetchTreatments();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not delete this treatment.");
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  const handleDownload = async (kind, filename) => {
    setBusy(true);
    try {
      const response = await downloadSpecialityTreatmentFile(
        kind,
        kind === "export" ? filterParams() : {}
      );
      saveBlob(response.data, filename);
    } catch (err) {
      toast.error("Download failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = null;
    if (!file) return;

    setBusy(true);
    try {
      const response = await importSpecialityTreatments(file);
      const summary = importSummary(response?.data);
      const failures = importFailures(response?.data);

      // Failed rows need their reasons, which a toast cannot hold.
      if (failures.length > 0) {
        setImportResult({ message: summary.message, failures });
      } else if (summary.level === "warning") {
        toast.warning(summary.message);
      } else {
        toast.success(summary.message);
      }

      setPageNumber(0);
      fetchTreatments();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "1rem 0",
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          label="Search treatments"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          sx={{ minWidth: 240 }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="speciality-filter-label">Speciality</InputLabel>
          <Select
            labelId="speciality-filter-label"
            label="Speciality"
            value={specialityFilter}
            onChange={(event) => {
              setSpecialityFilter(event.target.value);
              setPageNumber(0);
            }}
            inputProps={{ "data-testid": "speciality-filter" }}
          >
            <MenuItem value="">All</MenuItem>
            {specialityOptions.map((option) => (
              <MenuItem key={option._id} value={option._id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140, mr: "auto" }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPageNumber(0);
            }}
            inputProps={{ "data-testid": "status-filter" }}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value || "all"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          sx={btnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => handleDownload("template", "speciality_treatments_template.xlsx")}
        >
          Download Template
        </Button>

        <Button
          sx={btnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          Import
        </Button>
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          ref={fileInputRef}
          hidden
          data-testid="speciality-treatment-import-input"
          onChange={handleImport}
        />

        <Button
          sx={btnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => handleDownload("export", "speciality_treatments_export.xlsx")}
        >
          Export
        </Button>

        <Button
          sx={{ ...btnStyle, bgcolor: "#194AF5", color: "white", ":hover": {} }}
          variant="contained"
          disabled={busy}
          onClick={() => {
            setActiveTreatment({});
            setModalOpen(true);
          }}
        >
          Add Treatment
        </Button>
      </Box>

      <Listtable
        tableheader={TABLE_HEADERS}
        tablebody={rows}
        rowsPerPage={rowsPerPage}
        page={pageNumber}
        handleChangePage={(event, newPage) => setPageNumber(newPage)}
        handleChangeRowsPerPage={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPageNumber(0);
        }}
        totalCount={count}
        dataKeyName={TABLE_KEYS}
        tag="specialityTreatments"
      />

      {modalOpen && (
        <SpecialityTreatmentModal
          open={modalOpen}
          onClose={handleModalClose}
          data={activeTreatment}
          specialityOptions={specialityOptions}
        />
      )}

      <Dialog
        open={Boolean(importResult)}
        onClose={() => setImportResult(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Import complete</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>{importResult?.message}</DialogContentText>
          <Box sx={{ fontWeight: 600, mb: 1 }}>Failed rows:</Box>
          <Box
            component="ul"
            aria-label="Failed rows"
            sx={{ listStyle: "none", p: 0, m: 0, maxHeight: 320, overflowY: "auto" }}
          >
            {(importResult?.failures ?? []).map((item, index) => (
              <Box
                component="li"
                key={`${item.row}-${item.label}-${index}`}
                sx={{ py: 1, borderBottom: "1px solid #EEE", fontSize: "0.875rem" }}
              >
                <Box sx={{ fontWeight: 600 }}>
                  {item.row ? `Row ${item.row}` : "Row"}
                  {item.label ? ` — ${item.label}` : ""}
                </Box>
                <Box sx={{ color: "#d32f2f" }}>Reason: {item.reason}</Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete treatment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.name}"
            {deleteTarget?.specialityLabel ? ` (${deleteTarget.specialityLabel})` : ""}? It
            will stop appearing in every doctor's Specialty Treatments tab.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={loading || busy} sx={{ zIndex: 1200, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default SpecialityTreatmentMaster;
